import { useContext, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Message } from '@components';
import {
  BodyWrapper,
  Header,
  Loader,
  MainWrapper,
} from '@components';
import {
  locationDeleteMutation,
  siteDeleteMutation,
  siteQuery,
  siteMutation,
} from '@queries';
import { ChevronRightIcon } from '@svgs';

import { SiteBreadcrumbs } from '../components/SiteBreadcrumbs/SiteBreadcrumbs.jsx';
import SiteSidebar from '../components/SiteSidebar/SiteSidebar.jsx';

import { DeleteSite } from './DeleteSite/DeleteSite.jsx';
import { SiteSettingsForm } from './SiteSettingsForm/SiteSettingsForm.jsx';
import { errorsFromMutate } from '@Forms/utils.js';

export const SiteSettings = () => {
  const { t, i18n } = useTranslation();
  const { siteID } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState(null);
  const errorMessageRef = useRef(null);
  
  // scroll to error message when it's set
  useEffect(() => {
    if (errorMessage && errorMessageRef.current) {
      errorMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [errorMessage]);
  
  // get site
  const { data: siteData, isPending: siteIsPending } = useQuery(
    siteQuery(siteID),
  );

  // save site
  const mutation = useMutation(
    siteMutation(siteID, {
      onMutate: (data) => {
        setErrorMessage(null);
      },
      onSuccess: (data) => {
        // invalidate the query so it is fetched again
        queryClient.invalidateQueries({
          queryKey: ['sites', 'detail', siteID],
        });
      },
      onError: (error) => {
        const errorsList = errorsFromMutate(error);
        const allError = errorsList.find(({ field }) => field === '__all__');
        if(!!allError) {
          setErrorMessage(allError.error.message);
        }
      },
    }),
  );

  // delete site
  const deleteMutations = {
    location: useMutation(
      locationDeleteMutation(
        siteData?.location_ids.length == 1 ? siteData?.location_ids[0] : null,
        {
          onSuccess: () => {
            // invalidate the lists so its purged
            queryClient.invalidateQueries({ queryKey: ['locations', 'list'] });
          },
        },
      ),
    ),
    site: useMutation(
      siteDeleteMutation(siteID, {
        onSuccess: () => {
          // invalidate the lists so its purged
          queryClient.invalidateQueries({ queryKey: ['sites', 'list'] });
          // navigate to the list view
          navigate(`/dashboard/projects/${siteData.business_id}/sites`);
        },
      }),
    ),
  };

  // shared isPending status
  const mutationPending =
    mutation.isPending ||
    deleteMutations.location.isPending ||
    deleteMutations.site.isPending;
  return (
    <>
      <Header />
      <BodyWrapper>
        <SiteSidebar siteID={siteID} siteData={siteData} />
        <MainWrapper>
          <SiteBreadcrumbs siteData={siteData}>
            <li className="sb-flex">
              <span>{t('Website settings')}</span>
            </li>
          </SiteBreadcrumbs>
          <div className="sb-manage-content">
            <h1>{t('Website settings')}</h1>
            
            {siteIsPending && <Loader className="sb-loader sb-loader-large" />}
            {!siteIsPending && (
              <>
                {errorMessage && (
                  <div ref={errorMessageRef}>
                    <Message closable={true} messageStyle="error" title={'Something Went Wrong'} isFormMessage={false} message={errorMessage}/>
                  </div>
                )}
                <SiteSettingsForm
                  siteData={siteData}
                  disabled={mutationPending}
                  mutation={mutation}
                />
                <div className="sb-message-delete-section">
                  <DeleteSite
                    deleteMutations={deleteMutations}
                    siteData={siteData}
                  />
                </div>
              </>
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
